import { isBrowser } from '@firsttable/functions';
import { v4 as uuidv4 } from 'uuid';
import { GROWTHBOOK_DEVICE_ID_KEY } from './constants';

let fallbackDeviceId: string | undefined;
let fallbackDeviceIdSource: 'memory' | 'storage' | undefined;

const readStoredGrowthBookDeviceId = (): string | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  try {
    return window.localStorage.getItem(GROWTHBOOK_DEVICE_ID_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const writeStoredGrowthBookDeviceId = (deviceId: string): void => {
  if (!isBrowser) {
    return;
  }

  try {
    window.localStorage.setItem(GROWTHBOOK_DEVICE_ID_KEY, deviceId);
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
};

export const getGrowthBookDeviceId = ({
  amplitudeDeviceId,
  canUseStoredDeviceId,
  consentHydrated,
}: {
  amplitudeDeviceId?: string;
  canUseStoredDeviceId: boolean;
  consentHydrated: boolean;
}): string | undefined => {
  if (fallbackDeviceId) {
    if (!canUseStoredDeviceId && fallbackDeviceIdSource === 'storage') {
      fallbackDeviceId = undefined;
      fallbackDeviceIdSource = undefined;
    } else {
      return fallbackDeviceId;
    }
  }

  if (canUseStoredDeviceId) {
    const storedDeviceId = readStoredGrowthBookDeviceId();
    if (storedDeviceId) {
      fallbackDeviceId = storedDeviceId;
      fallbackDeviceIdSource = 'storage';
      return storedDeviceId;
    }
  }

  if (amplitudeDeviceId) {
    fallbackDeviceId = amplitudeDeviceId;
    fallbackDeviceIdSource = 'memory';
    return fallbackDeviceId;
  }

  if (!isBrowser || !consentHydrated) {
    return undefined;
  }

  fallbackDeviceId = uuidv4();
  fallbackDeviceIdSource = 'memory';
  return fallbackDeviceId;
};

export const persistGrowthBookFallbackDeviceId = (): void => {
  if (!fallbackDeviceId) {
    return;
  }

  writeStoredGrowthBookDeviceId(fallbackDeviceId);
};
